import { defineStore } from 'pinia'
import request, { type TResponse } from '@/utils/request'
import supabase from '@/utils/supabase'
import { User } from '@supabase/supabase-js'
import { checkUserLoggedIn, requestUserDetails, isUserKYCRequired } from './user.units'

export enum UserRoles {
    ADMIN = 'ADMIN',
    MODERATOR = 'MODERATOR',
    USER = 'USER'
}

export type AuthStage = 'register' | 'forgotPassword' | 'checkEmail' | 'getMessengers' | 'auth' | 'success'

export const MESSENGER_TYPES = ['whatsApp', 'telegram', 'discord', 'email', 'slack'] as const
export type MessengerType = typeof MESSENGER_TYPES[number]

export type UserDetails = {
    email: string
    messenger: MessengerType
    messengerAccount: string
    name: string
    website?: string,
    studioName?: string,
    countryCode?: string,
    readonly id: string,
    supabaseId: string,
    pic?: string,
    referralCode: string,
    invitedById?: string,
    howKnown?: string,
    deleted?: boolean,
}

const didUserVisitAuthLSKey = 'first_auth_visit'
const didUserAuthLSKey = 'first_auth_success'
const didUserRegisterSuccessfully = 'first_register_success'

export const useUserStore = defineStore('user', {
    state: () => ({
        role: UserRoles.USER,
        isUserLoggedIn: false,
        AuthStage: null as AuthStage | null,
        userDetails: null as UserDetails | null,
        isUserDetailsLoading: false,
        currentUser: null as User | null,
    }),
    getters: {
        isModerator: (state) => state.role === UserRoles.MODERATOR
            || state.role === UserRoles.ADMIN,
        authStage: (state): AuthStage | null => {
            if (state.AuthStage) { // TODO: rewrite. Can't use getter, setter and state simultaneously
                return state.AuthStage
            }
            if (state.isUserLoggedIn) {
                if (!state.currentUser?.email_confirmed_at) {
                    return 'checkEmail'
                }

                if (isUserKYCRequired(state.userDetails)) {
                    const shouldSendEvent = !window.localStorage
                        .getItem(didUserRegisterSuccessfully)

                    if (shouldSendEvent) {
                        window.dataLayer.push({
                            event: 'custom_event',
                            eventName: 'success_registration',
                            label: '',
                            action: '',
                        })
                        window.localStorage.setItem(didUserRegisterSuccessfully, '1')
                    }

                    return 'getMessengers'
                }

                return 'success'
            }
            return 'register'
        },
    },
    actions: {
        async logout() {
            this.setUserDetailsLoading(true)
            await supabase.auth.signOut()
            this.setAuthStage(null)
            this.setUserDetails(null)
            this.isUserLoggedIn = false
            this.setUserDetailsLoading(false)
        },
        login() {
            this.isUserLoggedIn = true
        },
        checkFirstAuthVisit() {
            try {
                const didUserAuth = window.localStorage.getItem(didUserAuthLSKey)
                const didUserVisitAuth = window.localStorage.getItem(didUserVisitAuthLSKey)
                if (!didUserVisitAuth && !didUserAuth) {
                    window.dataLayer.push({
                        event: 'custom_event',
                        eventName: 'first_auth_visit',
                        label: '',
                        action: 'visit',
                    })
                } if (!didUserVisitAuth) {
                    window.localStorage.setItem(didUserVisitAuthLSKey, '1')
                }
            } catch {
                // nothing we can do
            }
        },
        authSuccess() {
            try {
                const didUserAuth = window.localStorage.getItem(didUserAuthLSKey)
                const didUserVisitAuth = window.localStorage.getItem(didUserVisitAuthLSKey)
                if (didUserVisitAuth && !didUserAuth) {
                    window.dataLayer.push({
                        event: 'custom_event',
                        eventName: 'auth',
                        label: '',
                        action: 'auth',
                    })
                }
                window.localStorage.setItem(didUserAuthLSKey, '1')
            } catch {
                // nothing we can do
            }
        },
        checkUserRole() {
            return request<{role: UserRoles}>('/auth/role').then(({ data }) => {
                if (data && data.role) {
                    this.role = data.role
                } else {
                    this.role = UserRoles.USER
                }
                return this.role
            }, () => {
                this.role = UserRoles.USER
                return this.role
            })
        },
        async checkUserLoggedIn() {
            const isUserLoggedIn = await checkUserLoggedIn()
            this.isUserLoggedIn = isUserLoggedIn

            if (isUserLoggedIn) {
                const { data: { user } } = await supabase.auth.getUser()
                this.currentUser = user
                this.authSuccess()
                await this.getUserDetails()
            }
        },
        setAuthStage(stage: AuthStage | null) {
            this.AuthStage = stage // TODO: Rewrite. No two way data flow
        },
        async getUserDetails(): Promise<TResponse<UserDetails>> {
            const resp = await requestUserDetails()
            if (!resp.errors?.length) {
                const user = resp.data as UserDetails
                this.setUserDetails(user)
                if (user.deleted) {
                    this.setAuthStage('getMessengers')
                } else if (user.messenger) {
                    this.setAuthStage('success')
                } else {
                    this.setAuthStage('checkEmail')
                }
            }
            return resp
        },
        setUserDetails(user: UserDetails | null) {
            this.userDetails = user
        },
        async updateUserDetails(
            data: {userDetails: Omit<UserDetails, 'id' | 'supabaseId' | 'referralCode'>, inviteCode?: string | null },
        ): Promise<null | string> {
            const resp = await request<UserDetails>('/user', {
                method: 'post',
                body: JSON.stringify(data),
            })
            if (!resp.errors?.length) {
                this.setUserDetails(resp.data)
                if (data.userDetails.deleted) {
                    this.setAuthStage('getMessengers')
                } else if (data.userDetails.messenger) {
                    this.setAuthStage('success')
                } else {
                    this.setAuthStage('checkEmail')
                }
                return null
            }
            return resp.errors[0]
        },
        setUserDetailsLoading(isLoading: boolean) {
            this.isUserDetailsLoading = isLoading
        },
        getUserByReferralCode(referralCode: string) {
            return request<UserDetails>('/user/by-referral-code', {
                method: 'post',
                body: JSON.stringify({ referralCode }),
            })
        },
    },
})
